<script>
import get from 'lodash/get'
import mixinComponent from '../../Mixins/mixinComponent'
import { platformMixins } from '../../Mixins/platform'
import { COMPUTE_TYPE_AMOUNT, COMPUTE_TYPE_RENT } from '../../Constants/ComputeType'
import { CONTRACT_TYPE_LEASING } from '../../Constants/ContractType'
import { EQUIPMENT_CONDITION_NEW } from '../../Constants/EquipmentCondition'
import BlockContent from '../UI/Content/BlockContent.vue'
import FormCreator from '../UI/Form/FormCreator.vue'
import SimpleGroupSelect from '../UI/Form/SimpleGroupSelect.vue'
import LazyContent from '../UI/Loading/LazyContent.vue'
import FieldCreator from '../UI/Form/FieldCreator.vue'
import ButtonAll from '../UI/Button/ButtonAll.vue'

export default {
  components: {
    ButtonAll,
    FieldCreator,
    LazyContent,
    SimpleGroupSelect,
    FormCreator,
    BlockContent,
  },
  mixins: [mixinComponent, platformMixins],
  data: () => ({
    params: {
      entityById: true,
    },
    loadForm: false,
    loading: false,
    sending: false,
    simulation: null,
    formReady: false,
    formDef: null,
    displayTTC: false,
    formData: {},
  }),
  computed: {
    platformParams() {
      const platform = this.getPlatformById(this.params.platformId)
      return platform?.platformParams ?? []
    },
    rentColumnLabel() {
      if (this.simulation?.finance?.period?.id === 1) {
        return this.displayTTC
          ? this.$t('simulation.result.table.monthlyTtc')
          : this.$t('simulation.result.table.monthlyHt')
      }

      if ([COMPUTE_TYPE_AMOUNT, COMPUTE_TYPE_RENT].includes(this.simulation?.finance?.computeType)) {
        return this.displayTTC
          ? this.$t(`simulation.result.table.rentTtc.${this.simulation.finance.period?.id}`)
          : this.$t(`simulation.result.table.rentHt.${this.simulation.finance.period?.id}`)
      } else {
        return this.displayTTC
          ? this.$t('simulation.result.table.amountTtc')
          : this.$t('simulation.result.table.amountHT')
      }
    },
    initialRent() {
      if (this.simulation === null) {
        return 0
      }

      return this.computeRentAmount(this.simulation.finance.initialRent)
    },
    rent() {
      if (this.simulation === null) {
        return 0
      }

      const rent =
        this.simulation.finance.computeType === COMPUTE_TYPE_RENT
          ? parseFloat(this.simulation.finance.rent)
          : parseFloat(this.simulation.finance.rentInfo?.rent)
      return rent ?? 0
    },
    rentComputeType() {
      return this.simulation?.finance?.computeType === COMPUTE_TYPE_RENT
    },
    amountComputeType() {
      return this.simulation?.finance?.computeType === COMPUTE_TYPE_AMOUNT
    },
    amountFieldAvailable() {
      return this.isFieldAvailable('finance.simulationAmount')
    },
    rentFieldAvailable() {
      return this.isFieldAvailable('finance.rent')
    },
  },
  methods: {
    computeRentAmount(amount) {
      let taxRate = 1
      if (this.displayTTC) {
        taxRate = 1.2
      }

      return this.getRoundFloat(amount, taxRate, 100)
    },
    setSimulation(simulation) {
      this.simulation = simulation
    },
    setSending(sending) {
      this.sending = sending
    },
    setLoading(loading) {
      this.loading = loading
    },
    onFormReady({ def }) {
      this.formReady = !!def
      this.formDef = def
    },
    reloadForm() {
      this.$refs.calculator.get()
    },
    submit(computeType) {
      this.$refs.calculator.datas['finance.computeType'] = computeType
      this.$refs.calculator.datas['platformId'] = this.params.platformId
      this.$refs.calculator.datas['platformParamId'] = this.params.platformParamId

      this.$refs.calculator.inject['error'] = undefined
      if (
        this.$refs.calculator.datas['finance.contractType'] === CONTRACT_TYPE_LEASING &&
        !this.$refs.calculator.datas['finance.residualPercent']
      ) {
        this.addError({
          id: '#invalid-feedback-finance-residualPercent',
          message: 'simulation.fields.contractType.invalidResidualPercent',
        })
      }

      this.$refs.calculator.methods()
    },
    validate(datas, addError) {
      if (datas['finance.computeType'] === COMPUTE_TYPE_AMOUNT && !datas['finance.simulationAmount']) {
        addError('finance.simulationAmount')
      } else if (datas['finance.computeType'] === COMPUTE_TYPE_RENT && !datas['finance.rent']) {
        addError('finance.rent')
      }
    },
    reset() {
      this.simulation = null
    },
    setFormData(form) {
      this.formData = form
    },
    addError(error) {
      this.$refs.calculator.inject['error'] = this.$refs.calculator.inject['error'] ?? []
      this.$refs.calculator.inject['error'].push(error)
    },
    setDurationChoices(durations) {
      this.formDef.finance.duration.options.choices = durations.reduce((choices, duration) => {
        choices[duration] = duration
        return choices
      }, {})
    },
    onEquipmentTypeChange(value) {
      const equipmentType = this.formDef.equipment.equipmentType.options.choices.find(
        (equipmentType) => equipmentType.id === value,
      )

      const condition = this.$refs.calculator.datas['equipment.condition'] ?? EQUIPMENT_CONDITION_NEW
      const rawChoices =
        (condition === EQUIPMENT_CONDITION_NEW ? equipmentType.durations : equipmentType.durationsUsed) ?? ''
      const choices = rawChoices.split(',').filter((choice) => !!choice)

      if (choices.length > 0) {
        this.setDurationChoices(choices)
      }

      this.$refs.calculator.datas['equipment.productType'] = null
      if (equipmentType.productTypes.length === 1) {
        this.$refs.formsimulation.datas['equipment.productType'] = equipmentType.productTypes[0].id
        this.onProductTypeChange(equipmentType.productTypes[0].id)
      }
    },
    onProductTypeChange(value) {
      const productType = this.formDef.equipment.productType.options.choices.find(
        (productType) => productType.id === value,
      )

      const condition = this.$refs.calculator.datas['equipment.condition'] ?? EQUIPMENT_CONDITION_NEW
      const rawChoices =
        (condition === EQUIPMENT_CONDITION_NEW ? productType.durations : productType.durationsUsed) ?? ''
      const choices = rawChoices.split(',').filter((choice) => !!choice)

      if (choices.length > 0) {
        this.setDurationChoices(choices)
      }
    },
    onContractTypeChange(contractType) {
      this.$refs.calculator.def.finance.residualPercent.options.required = contractType === CONTRACT_TYPE_LEASING
      this.$refs.calculator.def.finance.residualValue.options.required = contractType === CONTRACT_TYPE_LEASING
    },
    isFieldAvailable(fieldName) {
      const fieldDef = get(this.formDef, fieldName)

      return fieldDef?.options.display
    },
    changePlatform(platformId) {
      this.params.platformId = platformId
      if (this.platformParams.length === 1) {
        this.changePlatformParam(this.platformParams[0].id)
      }
    },
    changePlatformParam(platformParamId) {
      this.params.platformParamId = platformParamId
    },
  },
  watch: {
    params: {
      deep: true,
      handler: async function (value) {
        if (!this.$refs.calculator) {
          return
        }

        this.params['platformId'] = value.platformId
        this.params['platformParamId'] = value.platformParamId
        if (this.formReady) {
          await this.$nextTick()
          this.reloadForm()
        }
      },
    },
  },
  mounted() {
    this.params.platformId = this.defaultPlatformId
    this.params.platformParamId = this.defaultPlatformParamId
    this.loadForm = true
  },
}
</script>
<template>
  <BlockContent :title="$t('calculator.menu')" icon="fas fa-calculator" title-level="5" color="info">
    <div :class="{ 'd-none': !!simulation }" data-test-id="calculator-form">
      <FormCreator
        v-if="loadForm"
        ref="calculator"
        endpoint="calculators"
        method="post"
        :params="params"
        class-to-add="d-none"
        src-error="calculator"
        @validate="validate"
        @global-build="onFormReady"
        @form-return="setSimulation"
        @is-loading="setLoading"
        @is-sending="setSending"
        @update-data="setFormData"
      />

      <div>
        <div class="row">
          <div v-if="platforms.length > 1 && hasRole('ROLE_CALCULATOR_SHOW_PLATFORM')" class="col-md-6">
            <SimpleGroupSelect
              :add-label="true"
              :label-text="$t('simulation.choice.entity')"
              v-model.number="params.platformId"
              @transmitted="changePlatform"
              :config-options="platforms"
              required
              data-test-id="platformId"
            />
          </div>

          <div v-if="platformParams.length > 1" class="col-md-6">
            <SimpleGroupSelect
              :add-label="true"
              :pre-select-value="false"
              :label-text="$t('simulation.choice.setting')"
              v-model.number="platformParamId"
              @transmitted="changePlatformParam"
              :config-options="platformParams"
              required
              data-test-id="platformParamId"
            />
          </div>
        </div>
        <LazyContent :loading="loading" :class="{ 'mt-4': loading }">
          <template v-if="formReady">
            <div class="row">
              <div class="col-md-6" v-if="formDef?.finance?.contractType.options.display">
                <FieldCreator
                  :form-data="formData"
                  name="finance.contractType"
                  form-ref="calculator"
                  domain="simulation"
                  label-top
                  dynamic-label
                  @get-select-change="onContractTypeChange"
                />
              </div>

              <div class="col-md-6" v-if="formDef?.finance?.residualPercent.options.display">
                <FieldCreator
                  :form-data="formData"
                  name="finance.residualPercent"
                  form-ref="calculator"
                  domain="simulation"
                  label-top
                  dynamic-label
                  type-value="float"
                />
              </div>
              <div class="col-md-6" v-if="formDef?.finance?.residualValue.options.display">
                <FieldCreator
                  :form-data="formData"
                  name="finance.residualValue"
                  form-ref="calculator"
                  domain="simulation"
                  label-top
                  dynamic-label
                />
              </div>

              <div class="col-md-6" v-if="formDef?.equipment?.equipmentType.options.display">
                <FieldCreator
                  :form-data="formData"
                  name="equipment.equipmentType"
                  form-ref="calculator"
                  domain="simulation"
                  label-top
                  dynamic-label
                  select-change="equipment.productType"
                  select-change-by-name="productTypes"
                  @get-select-change="onEquipmentTypeChange"
                />
              </div>
              <div class="col-md-6" v-if="formDef?.equipment?.productType.options.display">
                <FieldCreator
                  :form-data="formData"
                  name="equipment.productType"
                  form-ref="calculator"
                  domain="simulation"
                  label-top
                  dynamic-label
                  @get-select-change="onProductTypeChange"
                />
              </div>

              <div class="col-md-6" v-if="formDef?.finance?.duration.options.display">
                <FieldCreator
                  :form-data="formData"
                  name="finance.duration"
                  form-ref="calculator"
                  domain="simulation"
                  label-top
                  dynamic-label
                />
              </div>
              <div class="col-md-6" v-if="formDef?.finance?.period.options.display">
                <FieldCreator
                  :form-data="formData"
                  name="finance.period"
                  form-ref="calculator"
                  domain="simulation"
                  label-top
                  dynamic-label
                />
              </div>

              <div
                v-if="isFieldAvailable('finance.paymentMean') && isFieldAvailable('finance.companyCategory')"
                class="w-100"
              ></div>
              <div class="col-md-6" v-if="isFieldAvailable('finance.paymentMean')">
                <FieldCreator
                  :form-data="formData"
                  name="finance.paymentMean"
                  form-ref="calculator"
                  domain="simulation"
                  label-top
                  dynamic-label
                />
              </div>

              <div class="col-md-6" v-if="isFieldAvailable('finance.companyCategory')">
                <FieldCreator
                  :form-data="formData"
                  name="finance.companyCategory"
                  form-ref="calculator"
                  domain="simulation"
                  label-top
                  dynamic-label
                />
              </div>

              <div class="col-md-6" v-if="isFieldAvailable('finance.paymentTerm')">
                <FieldCreator
                  :form-data="formData"
                  name="finance.paymentTerm"
                  form-ref="calculator"
                  domain="simulation"
                  label-top
                  dynamic-label
                />
              </div>

              <div v-if="amountFieldAvailable && rentFieldAvailable" class="w-100"></div>
              <div class="col-md-6" v-if="amountFieldAvailable">
                <FieldCreator
                  :form-data="formData"
                  name="finance.simulationAmount"
                  form-ref="calculator"
                  domain="simulation"
                  label-top
                  dynamic-label
                />
              </div>
              <div class="col-md-6" v-if="rentFieldAvailable">
                <FieldCreator
                  :form-data="formData"
                  name="finance.rent"
                  form-ref="calculator"
                  domain="simulation"
                  label-top
                  dynamic-label
                />
              </div>
            </div>
          </template>
        </LazyContent>
      </div>
    </div>
    <div v-if="!!simulation" class="row">
      <div class="mt-2 col-md-12 d-flex flex-column">
        <div
          :class="{
            row: true,
            'mt-2': true,
            'order-0': amountComputeType,
            'order-2': rentComputeType,
            'text-success': rentComputeType,
          }"
        >
          <div class="col-6">
            <i class="fas fa-money-bill"></i>
            <span :class="{ 'font-weight-bold': rentComputeType }">
              {{ $t('simulation.fields.amount.label') }}
            </span>
          </div>
          <div class="col-4" data-test-id="calculator-simulation-amount">
            <span :class="{ 'font-weight-bold': rentComputeType }">
              {{ $n(Number(simulation.finance.simulationAmount), 'currency') }}
            </span>
          </div>
        </div>
        <div class="row mt-2 order-1">
          <div class="col-6">
            <i class="fas fa-calendar" style="margin-right: 6px"></i>
            {{ $t('simulation.fields.duration.label') }}
          </div>
          <div class="col-6">{{ simulation.finance.duration }} {{ $t('time.month') }}</div>
        </div>
        <div
          :class="{
            row: true,
            'mt-2': true,
            'order-0': rentComputeType,
            'order-2': amountComputeType,
            'text-success': amountComputeType,
          }"
        >
          <div class="col-6">
            <i class="fas fa-hand-holding-usd" style="margin-right: 3px"></i>
            <span :class="{ 'font-weight-bold': amountComputeType }">
              {{ rentColumnLabel }}
            </span>
          </div>
          <div class="col-4" data-test-id="calculator-simulation-rent">
            <span :class="{ 'font-weight-bold': amountComputeType }">
              {{ $n(rent, 'currency') }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <template v-if="formReady">
      <div class="row mt-2">
        <template v-if="!simulation">
          <div v-if="amountFieldAvailable" :class="{ 'col-6': rentFieldAvailable, 'col-12': !rentFieldAvailable }">
            <ButtonAll
              margin="no"
              label="simulation.input.button.computeRent"
              type="action"
              icon="calculator"
              width="full"
              data-test-id="calculator-compute-amount-button"
              :loading="sending"
              @click="submit('AMOUNT')"
            />
          </div>
          <div v-if="rentFieldAvailable" :class="{ 'col-6': amountFieldAvailable, 'col-12': !amountFieldAvailable }">
            <ButtonAll
              margin="no"
              label="simulation.input.button.computeAmount"
              type="action"
              icon="calculator"
              width="full"
              data-test-id="calculator-compute-rent-button"
              :loading="sending"
              @click="submit('RENT')"
            />
          </div>
        </template>
        <div class="col-12 mt-4" v-else>
          <ButtonAll
            margin="no"
            label="calculator.actions.reset"
            type="action"
            icon="calculator"
            width="full"
            data-test-id="calculator-redo-button"
            @click="reset"
          />
        </div>
      </div>
    </template>
  </BlockContent>
</template>
